/**
 * 生成メタデータ（どのモデル・プロンプト版が各ステップを生成したか）の
 * マージ／パース用の純粋ユーティリティ。
 *
 * ランタイム依存なし（Firestore SDK にも React にも依存しない）。
 * 本番コードとユニットテストの両方から利用する。
 */
import type { CharacterData, GenerationMetadata, GenerationStepMetadata } from '../types';

export type { GenerationMetadata, GenerationStepMetadata } from '../types';

/** ワイヤーフォーマット／Firestore 上のステップキー（順序も固定） */
export const GENERATION_STAGES = ['analysis', 'diary', 'image'] as const;
export type GenerationStage = (typeof GENERATION_STAGES)[number];

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;
const isCanonicalIsoInstant = (v: unknown): v is string => {
  if (typeof v !== 'string') return false;
  const timestamp = Date.parse(v);
  return !Number.isNaN(timestamp) && new Date(timestamp).toISOString() === v;
};

/** 1ステップ分のメタデータとして妥当かどうか */
export const isGenerationStepMetadata = (v: unknown): v is GenerationStepMetadata => {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return isNonEmptyString(o.model) && isNonEmptyString(o.promptVersion) && isCanonicalIsoInstant(o.generatedAt);
};

/**
 * 複数段階（analyze → generate）の結果をマージする。
 * 後の引数が同じステップを持つ場合はそちらを優先し、それ以外のステップは失わない。
 * 不正なステップは落とし、既知のフィールドだけをコピーする（プロンプト本文などは持ち込まない）。
 * 全て空なら undefined（既存カードと同じ「メタデータなし」状態）。
 */
export const mergeGenerationMetadata = (
  ...parts: Array<GenerationMetadata | null | undefined>
): GenerationMetadata | undefined => {
  const merged: GenerationMetadata = {};
  for (const part of parts) {
    if (!part || typeof part !== 'object') continue;
    for (const stage of GENERATION_STAGES) {
      const step = (part as Record<string, unknown>)[stage];
      if (isGenerationStepMetadata(step)) {
        merged[stage] = { model: step.model, promptVersion: step.promptVersion, generatedAt: step.generatedAt };
      }
    }
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
};

/**
 * HTTP レスポンスや Firestore ドキュメントの生値から GenerationMetadata を安全に取り出す。
 * 不正・欠落は undefined（後方互換）。不正なステップだけ落とし、妥当なステップは残す。
 */
export const parseGenerationMetadata = (raw: unknown): GenerationMetadata | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  return mergeGenerationMetadata(raw as GenerationMetadata);
};

/** analyze アクションのレスポンスを、characterData と generationMetadata に分離した結果 */
export interface AnalysisResult {
  characterData: CharacterData;
  /** analysis は常に、diary は日記生成に成功したときだけ含まれる。旧サーバーからは undefined */
  generationMetadata?: GenerationMetadata;
}

/**
 * analyze アクションのレスポンス（解析結果 + generationMetadata）を分離する。
 * characterData に generationMetadata を混ぜない（Firestore の characterData を汚さない）。
 */
export const splitAnalysisResponse = (raw: unknown): AnalysisResult => {
  const { generationMetadata, ...characterData } = (raw ?? {}) as CharacterData & { generationMetadata?: unknown };
  return {
    characterData: characterData as CharacterData,
    generationMetadata: parseGenerationMetadata(generationMetadata),
  };
};
